import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Noe gikk galt. Prøv igjen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AdminLogin = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Innlogging feilet");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "mt-2 w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 transition-colors duration-200 focus:border-sky-600 focus:outline-none";

  return (
    <div className="blueprint-grid flex min-h-screen items-center justify-center bg-slate-50 px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="corner-frame rounded-lg border border-slate-200 bg-white p-8 shadow-md sm:p-10">
          <div className="flex flex-col items-center text-center">
            <img src="/logo-mark.png" alt="Norgesconsult logo" className="h-14 w-auto object-contain" />
            <h1 className="mt-4 font-display text-xl font-bold tracking-tight text-slate-900" data-testid="admin-login-title">
              Norgesconsult Admin
            </h1>
            <p className="mt-1 text-sm text-slate-500">Logg inn for å lese henvendelser og administrere innhold</p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="al-email" className="font-mono text-xs uppercase tracking-widest text-slate-500">
                E-post
              </label>
              <input
                id="al-email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-input-email"
                className={inputCls}
                placeholder="post@norgesconsult.no"
              />
            </div>
            <div>
              <label htmlFor="al-password" className="font-mono text-xs uppercase tracking-widest text-slate-500">
                Passord
              </label>
              <input
                id="al-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-input-password"
                className={inputCls}
                placeholder="••••••••••"
              />
            </div>

            {error && (
              <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="admin-login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-sky-600 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
              {loading ? "Logger inn ..." : "Logg inn"}
            </button>
          </form>
        </div>

        <Link
          to="/"
          data-testid="admin-login-back-link"
          className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 transition-colors hover:text-sky-700"
        >
          <ArrowLeft size={15} />
          Tilbake til norgesconsult.no
        </Link>
      </div>
    </div>
  );
};

export default AdminLogin;
